import { computed } from "vue";
import { useRoute } from "vue-router";
import { clientPageStore } from "@/store/client-page";
import { projectPageStore } from "@/store/project-page";

/**
 * Уровень доступа к блоку контактов (поле PersonAccess клиента/проекта):
 *   0 — доступ запрещён, 1 — только чтение, 2 — полный доступ.
 *
 * Доступ вычисляется напрямую из route.query + сторов страниц
 * (openedClient / openedProject). Компонент ClientsPanelListItem общий для всех
 * трёх страниц, поэтому единая точка вычисления гейтит и его поэлементные кнопки.
 *
 * Доступ применяется на страницах Клиента и Проекта. На Главной блок контактов
 * не ограничивается — там всегда возвращается 2 (полный доступ).
 *
 * По умолчанию 2 (полный доступ): вне страниц клиента/проекта и пока сущность
 * ещё грузится.
 */
export function useContactsAccess() {
	const route = useRoute();
	const clientStore = clientPageStore();
	const projectStore = projectPageStore();

	const personAccessLevel = computed<number>(() => {
		const hasId = route.query.id != null && route.query.id !== "";
		if (route.query.page === "client" && hasId) {
			return clientStore.openedClient?.PersonAccess ?? 2;
		}
		if (route.query.page === "project" && hasId) {
			return projectStore.openedProject?.PersonAccess ?? 2;
		}
		return 2;
	});

	const isContactsReadonly = computed(() => personAccessLevel.value === 1);
	const isContactsDenied = computed(() => personAccessLevel.value === 0);

	const deniedText = computed(() =>
		route.query.page === "project"
			? "У вас отсутствует доступ к контактам проекта"
			: "У вас отсутствует доступ к контактам клиента"
	);

	return { personAccessLevel, isContactsReadonly, isContactsDenied, deniedText };
}
